/**
 * Notification REST API Service
 *
 * Pure async functions for user notification endpoints.
 * No React dependencies -- consumed by TanStack Query hooks.
 *
 * @module entities/notification/services/notificationApi
 */

import { apiClient } from '@lib/api/client';
import { userEndpoints } from '@lib/api/config';
import type {
  NotificationListFilters,
  NotificationListResponse,
  UnreadCountResponse,
  MarkReadResponse,
  MarkAllReadResponse,
} from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// API Service
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch paginated notification list with optional filters
 *
 * @param filters - category, status, cursor, limit
 * @returns Paginated notification list with unread count
 */
export async function getNotifications(
  filters: NotificationListFilters = {}
): Promise<NotificationListResponse> {
  const params: Record<string, string | number | boolean> = {};

  if (filters.category) params.category = filters.category;
  if (filters.status) params.status = filters.status;
  if (filters.cursor) params.cursor = filters.cursor;
  if (filters.limit) params.limit = filters.limit;

  const response = await apiClient.get<NotificationListResponse>(
    userEndpoints.notifications.list(),
    params
  );

  if (!response.success || !response.data) {
    throw new Error(response.message || 'Failed to fetch notifications');
  }

  // apiClient wraps the full backend JSON body as response.data
  // Backend returns: { success, data: { notifications, nextCursor, totalUnread } }
  const backendBody = response.data as unknown as { data: NotificationListResponse };
  return backendBody.data;
}

/**
 * Fetch unread notification count for badge display
 */
export async function getUnreadCount(): Promise<UnreadCountResponse> {
  const response = await apiClient.get<UnreadCountResponse>(
    userEndpoints.notifications.unreadCount()
  );

  if (!response.success || !response.data) {
    throw new Error(response.message || 'Failed to fetch unread count');
  }

  // apiClient wraps the full backend JSON body as response.data
  // Backend returns: { success, data: { count } }
  const backendBody = response.data as unknown as { data: UnreadCountResponse };
  return backendBody.data;
}

/**
 * Mark a single notification as read
 *
 * @param id - Notification UUID
 */
export async function markAsRead(id: string): Promise<MarkReadResponse> {
  const response = await apiClient.patch<MarkReadResponse>(
    userEndpoints.notifications.markRead(id)
  );

  if (!response.success || !response.data) {
    throw new Error(response.message || 'Failed to mark notification as read');
  }

  // apiClient wraps the full backend JSON body as response.data
  const backendBody = response.data as unknown as { data: MarkReadResponse };
  return backendBody.data;
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<MarkAllReadResponse> {
  const response = await apiClient.patch<MarkAllReadResponse>(
    userEndpoints.notifications.markAllRead()
  );

  if (!response.success || !response.data) {
    throw new Error(
      response.message || 'Failed to mark all notifications as read'
    );
  }

  // apiClient wraps the full backend JSON body as response.data
  const backendBody = response.data as unknown as { data: MarkAllReadResponse };
  return backendBody.data;
}

/**
 * Dismiss (delete) a notification
 *
 * @param id - Notification UUID
 */
export async function dismissNotification(id: string): Promise<void> {
  const response = await apiClient.delete(
    userEndpoints.notifications.dismiss(id)
  );

  if (!response.success) {
    throw new Error(response.message || 'Failed to dismiss notification');
  }
}
